import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { RadixSelect, RadixInput } from '@/components/radix';
import { t } from 'i18next';
import { usePlaygroundFormRegistry } from '../../hooks/usePlaygroundFormRegistry';
import DaySelector, { type DaySelectorLabel } from './DaySelector';
import {
    buildCompositeFromDaySelectorValue,
    DURATION_HOURS_TOUCHED,
    getDaySelectorValueFromComposite,
} from './durationPeriodUtils';
import { DURATION_OPERATORS_WITHIN_NEXT } from '@/data/Marketing/marketingDurationOperators';

interface GiftCardBalanceCompositeValues {
    balanceOperator?: '>' | '<' | '=';
    balanceAmount?: number | null;
    expirationPeriod?: [number | null, number | null];
    selectedDurationFormat?: DaySelectorLabel;
    operator?: string;
}

interface GiftCardBalanceInputProps {
    value: number | null | undefined;
    compositeValues?: GiftCardBalanceCompositeValues;
    conditionInstanceId: string;
    /** When true, only expiration period (within/next). When false, only balance amount + operator. */
    hasExpiration?: boolean;
}

const balanceOperatorOptions = [
    { label: 'Greater than', value: '>' },
    { label: 'Less than', value: '<' },
    { label: 'Equal to', value: '=' },
];

const balanceValidationSchema = Yup.object().shape({
    balanceOperator: Yup.string().oneOf(['>', '<', '=']).required(t('Marketing.BalanceOperatorRequired')),
    balanceAmount: Yup.number()
        .required(t('Marketing.BalanceAmountRequired'))
        .typeError(t('Marketing.BalanceAmountMustBeNumber'))
        .min(0, t('Marketing.BalanceAmountMustBePositive')),
});

const expirationValidationSchema = Yup.object().shape({
    duration: Yup.object().shape({
        hours: Yup.number()
            .nullable()
            .test('required', t('Marketing.DaysRequired'), (val) => val !== null && val !== undefined),
    }),
});

const GiftCardBalanceInput = ({
    value,
    compositeValues,
    conditionInstanceId,
    hasExpiration = false,
}: GiftCardBalanceInputProps) => {
    const { register, unregister } = usePlaygroundFormRegistry();
    const balanceOperator = compositeValues?.balanceOperator || '>';
    const balanceAmount = compositeValues?.balanceAmount ?? (typeof value === 'number' ? value : null);
    const hasPersistedExpiration =
        Array.isArray(compositeValues?.expirationPeriod) &&
        compositeValues.expirationPeriod[0] !== null &&
        compositeValues.expirationPeriod[0] !== undefined;
    const initialDuration = hasExpiration
        ? getDaySelectorValueFromComposite(compositeValues, {
              includeOperator: true,
              defaultOperator: compositeValues?.operator ?? 'WITHIN_NEXT_HOURS',
          })
        : getDaySelectorValueFromComposite(undefined);
    // Unconfigured expiry must not look valid from DaySelector defaults (hours: 7)
    if (hasExpiration && !hasPersistedExpiration) {
        initialDuration.hours = null;
    }

    const formik = useFormik({
        initialValues: {
            balanceOperator,
            balanceAmount,
            duration: initialDuration,
        },
        validationSchema: hasExpiration ? expirationValidationSchema : balanceValidationSchema,
        enableReinitialize: true,
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: () => {
            // Validation handled automatically
        },
    });

    useEffect(() => {
        register(conditionInstanceId, {
            validate: async () => {
                const errors = await formik.validateForm();
                const isValid = Object.keys(errors).length === 0;
                if (!isValid) {
                    formik.setTouched(
                        hasExpiration
                            ? { duration: DURATION_HOURS_TOUCHED }
                            : { balanceOperator: true, balanceAmount: true },
                    );
                    return false;
                }
                return true;
            },
            getValues: () => {
                if (hasExpiration) {
                    return buildCompositeFromDaySelectorValue(formik.values.duration, 'expirationPeriod');
                }
                return {
                    balanceOperator: formik.values.balanceOperator,
                    balanceAmount: formik.values.balanceAmount,
                };
            },
        });

        return () => {
            unregister(conditionInstanceId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conditionInstanceId, formik.values, register, unregister, hasExpiration]);

    const handleBalanceOperatorChange = (val: string) => {
        formik.setFieldValue('balanceOperator', val);
    };

    const handleBalanceAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const amount = e.target.value ? Number(e.target.value) : null;
        formik.setFieldValue('balanceAmount', amount);
    };

    if (hasExpiration) {
        return (
            <div className="flex flex-col gap-4">
                <DaySelector
                    label={t('Marketing.ExpirationPeriod')}
                    showOperator
                    operatorOptions={DURATION_OPERATORS_WITHIN_NEXT}
                    value={formik.values.duration}
                    onChange={(next) => formik.setFieldValue('duration', next)}
                    summaryNoteKey="Marketing.DaySelectorNoteGiftCardExpiry"
                    showError={Boolean(
                        formik.touched.duration &&
                            (formik.errors.duration as { hours?: string } | undefined)?.hours,
                    )}
                    error={
                        typeof (formik.errors.duration as { hours?: string } | undefined)?.hours === 'string'
                            ? (formik.errors.duration as { hours?: string }).hours
                            : t('Marketing.DaysRequired')
                    }
                />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="w-full">
                <label className="block text-sm font-medium text-text-primary mb-2">
                    {t('Marketing.BalanceCondition')}
                </label>
                <RadixSelect
                    options={balanceOperatorOptions.map((opt) => ({
                        label: opt.label,
                        value: opt.value,
                    }))}
                    value={formik.values.balanceOperator}
                    onValueChange={handleBalanceOperatorChange}
                    placeholder={t('Marketing.SelectBalanceCondition')}
                    className="w-full"
                />
                {formik.touched.balanceOperator && formik.errors.balanceOperator && (
                    <div className="text-sm text-red-500 mt-1">
                        {typeof formik.errors.balanceOperator === 'string'
                            ? formik.errors.balanceOperator
                            : t('Marketing.BalanceOperatorRequired')}
                    </div>
                )}
            </div>

            <div className="w-full">
                <label className="block text-sm font-medium text-text-primary mb-2">{t('Common.Amount')}</label>
                <RadixInput
                    type="number"
                    value={formik.values.balanceAmount !== null ? String(formik.values.balanceAmount) : ''}
                    onChange={handleBalanceAmountChange}
                    placeholder="1000"
                    className="w-full"
                />
                {formik.touched.balanceAmount && formik.errors.balanceAmount && (
                    <div className="text-sm text-red-500 mt-1">
                        {typeof formik.errors.balanceAmount === 'string'
                            ? formik.errors.balanceAmount
                            : t('Marketing.BalanceAmountRequired')}
                    </div>
                )}
            </div>
        </div>
    );
};

export default GiftCardBalanceInput;
